export interface Rung {
  rung: string;
  resolution: string;
  bandwidth: number;
  variantUri: string;
}

/** Max rung bitrate (bits/s) offerable for offline caching. The Phase-0 seeking
 *  spike showed a reassembled rung scrubs cleanly up to ~50 Mbps, but the
 *  80 Mbps "Max" 4K rung freezes when cached. 60 Mbps keeps every scrubbable
 *  rung (incl. 50 Mbps 4K) and excludes only the 80 Mbps Max tier. */
export const OFFLINE_MAX_RUNG_BITRATE = 60_000_000;

/** The rungs that may be cached offline: those at or below the bitrate cap,
 *  sorted largest bandwidth first (so a picker can present "largest first"
 *  regardless of master-playlist line order). */
export function offlineSelectableRungs(rungs: Rung[]): Rung[] {
  return rungs
    .filter((r) => r.bandwidth <= OFFLINE_MAX_RUNG_BITRATE)
    .sort((a, b) => b.bandwidth - a.bandwidth);
}

/** Parse an HLS master playlist into its variant rungs. Each #EXT-X-STREAM-INF
 *  tag is followed by a variant-playlist URI line. The Worker may append
 *  ?team= to that URI for admin cross-team viewing; we resolve it to an
 *  absolute URL against the master URL and derive the rung name from the
 *  directory segment that contains the variant playlist in the resolved
 *  pathname (e.g. ".../v2160p25/playlist.m3u8" -> "v2160p25"). Using the
 *  resolved pathname prevents an absolute variant URI from yielding a bogus
 *  rung like "https:". */
export function parseMasterRungs(masterText: string, masterUrl: string): Rung[] {
  const lines = masterText.split(/\r?\n/);
  const rungs: Rung[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!.trim();
    if (!line.startsWith("#EXT-X-STREAM-INF:")) continue;
    const uriLine = (lines[i + 1] ?? "").trim();
    if (uriLine === "" || uriLine.startsWith("#")) continue;

    const bandwidth = Number(/BANDWIDTH=(\d+)/.exec(line)?.[1]) || 0;
    const resolution = /RESOLUTION=([0-9x]+)/.exec(line)?.[1] ?? "";
    const url = new URL(uriLine, masterUrl);
    const variantUri = url.toString();
    // Rung name = the directory segment that contains the variant playlist
    // (".../<rung>/playlist.m3u8"). Derived from the resolved pathname so an
    // absolute variant URI can't yield a bogus rung like "https:". A missing
    // BANDWIDTH attribute yields bandwidth: 0 (a degenerate rung); callers
    // treat 0 as lowest-quality.
    const segs = url.pathname.split("/").filter((p) => p !== "");
    const rung = segs.length >= 2 ? segs[segs.length - 2]! : (segs[0] ?? "");
    rungs.push({ rung, resolution, bandwidth, variantUri });
  }
  return rungs;
}
